/**
 * The viewer's entry point — bundled by `scripts/build.mjs` into
 * `public/assets/viewer.js` and loaded by `functions/v/[vennId].ts`'s
 * generated HTML. This file is the only place production `window`/
 * `document` globals are touched; everything it calls into
 * (hashSecret.ts, device.ts, api.ts, arrival.ts) takes them as injected
 * dependencies instead, which is what keeps those files unit-testable.
 */

import { windowFragmentSource } from './hashSecret.ts';
import { windowDeviceStorage } from './device.ts';
import { applyTheme } from './theme.ts';
import { ArrivalController } from './arrival.ts';
import { CLOSED_COPY } from './copy.ts';

function boot(): void {
  applyTheme(document.documentElement);
  const root = document.querySelector<HTMLElement>('[data-role="stage-root"]');
  if (!root) return; // not a viewer page at all (e.g. the landing page)

  // The vennId comes from the DOM, not a client-side re-parse of
  // `window.location.pathname` (Ferruccio's gate, PR #83, I3/nit,
  // fixed together): the server (functions/v/[vennId].ts) performs the
  // single authoritative decode and publishes the DECODED vennId here as
  // `data-venn-id`. Note what the server does NOT get for free —
  // Cloudflare Pages Functions hands `context.params` to that route
  // still percent-encoded, so the decode is an explicit step there (and
  // a malformed escape is answered as a 404 before this markup is ever
  // emitted). What that buys this file is unchanged: no decode step
  // here at all, rather than merely one wrapped in a try/catch. The old
  // client-side `decodeURIComponent(...)` THREW on a malformed
  // percent-escape (`/v/%zz`), killing `boot()` silently and freezing
  // the stage on its static server-rendered 'bloom' markup forever;
  // there is no decode left here to throw. The value read below is
  // decoded, which is precisely why `api.ts` may — and must —
  // `encodeURIComponent` it when building its request URLs.
  const vennId = root.dataset.vennId;
  if (!vennId) {
    // Neither a real venn (Cloudflare's own routing wouldn't have
    // rendered this markup for a genuinely non-matching path) nor
    // recoverable — the honest treatment is the same CLOSED stage a
    // definitively dead link gets, never silence.
    const stage = root.querySelector<HTMLElement>('[data-role="stage"]');
    const statusLine = root.querySelector<HTMLElement>(
      '[data-role="status-line"]',
    );
    if (stage) stage.dataset.state = 'closed';
    if (statusLine) statusLine.textContent = CLOSED_COPY;
    return;
  }

  const controller = new ArrivalController(root, {
    vennId,
    fragmentSource: windowFragmentSource(window),
    deviceStorage: windowDeviceStorage(window),
    fetchImpl: window.fetch.bind(window),
    scheduleRetry: (run, delayMs) => {
      const id = window.setTimeout(run, delayMs);
      return () => window.clearTimeout(id);
    },
  });
  controller.start();

  // A bfcache-restored page can show the address bar as it looked at
  // freeze time, fragment and all — `event.persisted` distinguishes
  // "restored from bfcache" from an ordinary `pageshow` (which every
  // page also fires once on a normal load; re-stripping/restarting then
  // is harmless, but gated on `persisted` anyway to say precisely what
  // this is for). `restripAddressBar()` and `restart()` are two
  // separate, explicit operations — see arrival.ts's own module comment
  // ("BFCACHE, RECONSIDERED") for why a genuine restart, not just a
  // strip, is what actually keeps this page's own "never a silently
  // stuck bloom" promise true across a bfcache round trip.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    controller.restripAddressBar();
    controller.restart();
  });

  // Stops wasted work (an in-flight fetch, a pending retry timer) while
  // this tab is hidden or frozen into the bfcache — see arrival.ts's own
  // `teardown()` for what it deliberately does NOT do (wipe the secret)
  // and why.
  window.addEventListener('pagehide', () => {
    controller.teardown();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
